import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import api from '@/services/api';
import { toast } from 'sonner';

interface UploadResult {
  id: number;
  filename: string;
  categories: string[];
  framework_relevance: Record<string, { score: number; reason: string }>;
  file_size: number;
  processed: boolean;
  message: string;
  processing?: boolean; // Local state for tracking
}

interface RepositoryUploadProps {
  onUploadComplete: () => void;
}

export function RepositoryUpload({ onUploadComplete }: RepositoryUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [progress, setProgress] = useState(0);
  const [processingIds, setProcessingIds] = useState<Set<number>>(new Set());

  const pollDocumentStatus = useCallback(async (docId: number) => {
    try {
      const response = await api.get(`/repository/documents/${docId}`);
      const doc = response.data;
      
      if (doc.processed) {
        // Update result to show processed
        setUploadResults(prev => 
          prev.map(r => r.id === docId ? { ...r, processed: true, processing: false } : r)
        );
        setProcessingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(docId);
          return newSet;
        });
        toast.success(`${doc.filename} processing complete!`);
        onUploadComplete();
      } else {
        // Still processing, poll again in 2 seconds
        setTimeout(() => pollDocumentStatus(docId), 2000);
      }
    } catch (error) {
      console.error('Failed to poll document status:', error);
    }
  }, [onUploadComplete]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      try {
        setUploading(true);
        setProgress(0);
        setUploadResults([]);

        const results: UploadResult[] = [];

        for (let i = 0; i < acceptedFiles.length; i++) {
          const file = acceptedFiles[i];
          if (!file) continue;
          
          try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await api.post('/repository/upload', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });

            const result = { ...response.data, processing: !response.data.processed };
            results.push(result);
            setProgress(Math.round(((i + 1) / acceptedFiles.length) * 100));
            
            toast.success(`${file.name} uploaded`);
            
            // Start polling if not processed
            if (!response.data.processed) {
              setProcessingIds(prev => new Set(prev).add(response.data.id));
              pollDocumentStatus(response.data.id);
            }
          } catch (error: any) {
            console.error(`Failed to upload ${file.name}:`, error);
            toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || error.message}`);
          }
        }

        setUploadResults(results);
        
        if (results.length > 0) {
          onUploadComplete();
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Upload failed');
      } finally {
        setUploading(false);
      }
    },
    [onUploadComplete, pollDocumentStatus]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'text/plain': ['.txt'],
      'message/rfc822': ['.eml'],
      'application/vnd.ms-outlook': ['.msg'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    disabled: uploading,
  });

  const getFrameworkColor = (framework: string) => {
    const colors: Record<string, string> = {
      DORA: 'bg-blue-500',
      ISO_27001: 'bg-green-500',
      SOC2: 'bg-purple-500',
      ESG: 'bg-emerald-500',
      RFP: 'bg-orange-500',
      QUESTIONNAIRE: 'bg-pink-500',
      SERVICE_PROPOSAL: 'bg-teal-500',
    };
    return colors[framework] || 'bg-gray-500';
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle>Upload to Repository</CardTitle>
          <CardDescription>
            Upload reference documents once, reuse across all projects. Documents are automatically
            categorized for relevant frameworks.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors
              ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
              ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <input {...getInputProps()} />
            
            {uploading ? (
              <div className="space-y-4">
                <Loader2 className="h-12 w-12 mx-auto text-blue-500 animate-spin" />
                <p className="text-lg font-medium">Processing documents...</p>
                <Progress value={progress} className="w-full max-w-md mx-auto" />
                <p className="text-sm text-gray-500">{progress}% complete</p>
              </div>
            ) : (
              <div className="space-y-4">
                <Upload className="h-12 w-12 mx-auto text-gray-400" />
                <div>
                  <p className="text-lg font-medium">
                    {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
                  </p>
                  <p className="text-sm text-gray-500">or click to browse</p>
                </div>
                <p className="text-xs text-gray-400">
                  Supported: PDF, DOCX, XLSX, CSV, TXT, EML, MSG, JPG, PNG (max 50MB)
                </p>
              </div>
            )}
          </div>

          {uploadResults.length > 0 && (
            <div className="mt-6 space-y-4">
              <h3 className="text-sm font-semibold">Upload Results</h3>
              {uploadResults.map((result, idx) => (
                <Alert key={idx} className={result.processing ? "border-blue-200 bg-blue-50" : "border-green-200 bg-green-50"}>
                  {result.processing ? (
                    <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  )}
                  <AlertDescription>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{result.filename}</span>
                        <Badge variant="outline">
                          {(result.file_size / 1024 / 1024).toFixed(2)} MB
                        </Badge>
                      </div>
                      
                      {result.categories.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {result.categories.map((cat) => (
                            <Badge
                              key={cat}
                              className={`${getFrameworkColor(cat)} text-white`}
                            >
                              {cat}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-gray-600">
                        {result.processing ? 'Processing and extracting data...' : result.message}
                      </p>
                      
                      {/* Framework Relevance Scores */}
                      {Object.keys(result.framework_relevance || {}).length > 0 && (
                        <div className="mt-2 space-y-1">
                          {Object.entries(result.framework_relevance)
                            .filter(([_, data]) => data.score >= 5)
                            .map(([framework, data]) => (
                              <div key={framework} className="text-xs">
                                <span className="font-medium">{framework}:</span>{' '}
                                <span className="text-gray-600">
                                  {data.score}/10 - {data.reason}
                                </span>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Upload className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-sm font-medium">Upload Once</p>
                <p className="text-xs text-gray-500">Reuse across all projects</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-sm font-medium">Auto-Categorized</p>
                <p className="text-xs text-gray-500">AI determines relevance</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-sm font-medium">Pre-Extracted</p>
                <p className="text-xs text-gray-500">Fast project creation</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
